import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, UserCircle, Mail, Phone, Stethoscope, Building2 } from 'lucide-react';
import { doctorsService, type CreateDoctorData, type UpdateDoctorData, type DoctorWithClinics } from '@/services/api/doctors.service';
import { useAuth } from '@/hooks/useAuth';
import { useContext } from '@/hooks/useContext';
import { clinicsService } from '@/services/api/clinics.service';
import type { Doctor, Clinic } from '@/types/models';
import { toast } from 'react-hot-toast';

export default function DoctorsPage() {
  const navigate = useNavigate();
  const { clinic, clinicId, doctor, clearDoctor } = useContext();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorWithClinics | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState<Doctor | null>(null);
  const [isClinicsModalOpen, setIsClinicsModalOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    // Redirect to clinic selection if no clinic is selected
    if (!clinicId) {
      toast.error('Selecione uma clínica primeiro');
      navigate('/clinicas');
      return;
    }
    loadDoctors();
  }, [currentPage, clinicId, navigate]);

  const loadDoctors = async () => {
    if (!clinicId) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await doctorsService.getAll({
        page: currentPage,
        limit,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      
      // Filter doctors by current clinic
      const clinicDoctors = response.data.filter(doctor => 
        doctor.clinics && doctor.clinics.includes(clinicId)
      );
      
      setDoctors(clinicDoctors);
      // Adjust pagination based on filtered results
      setTotalPages(Math.ceil(clinicDoctors.length / limit));
      setTotal(clinicDoctors.length);
    } catch (error) {
      console.error('Error loading doctors:', error);
      toast.error('Erro ao carregar médicos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedDoctor(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (doctor: Doctor) => {
    try {
      // Load full doctor data with clinics
      const fullDoctor = await doctorsService.getById(doctor.id);
      setSelectedDoctor(fullDoctor);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading doctor:', error);
      toast.error('Erro ao carregar dados do médico');
    }
  };

  const handleDelete = (doctor: Doctor) => {
    setDoctorToDelete(doctor);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!doctorToDelete) return;

    try {
      // Check if we're deleting the currently selected doctor
      const isDeletingCurrentDoctor = doctor?.id === doctorToDelete.id;

      await doctorsService.delete(doctorToDelete.id);
      toast.success('Médico excluído com sucesso!');

      // If deleting the current doctor, clear doctor context and redirect to doctor selection
      if (isDeletingCurrentDoctor) {
        clearDoctor();
        navigate('/medicos');
      }

      loadDoctors();
      setIsDeleteDialogOpen(false);
      setDoctorToDelete(null);
    } catch (error) {
      console.error('Error deleting doctor:', error);
      toast.error('Erro ao excluir médico');
    }
  };

  const handleToggleStatus = async (doctor: Doctor) => {
    try {
      await doctorsService.toggleStatus(doctor.id);
      toast.success(`Médico ${doctor.isActive ? 'desativado' : 'ativado'} com sucesso!`);
      loadDoctors();
    } catch (error) {
      console.error('Error toggling doctor status:', error);
      toast.error('Erro ao alterar status do médico');
    }
  };

  const handleManageClinics = async (doctor: Doctor) => {
    try {
      const fullDoctor = await doctorsService.getById(doctor.id);
      setSelectedDoctor(fullDoctor);
      setIsClinicsModalOpen(true);
    } catch (error) {
      console.error('Error loading doctor:', error);
      toast.error('Erro ao carregar dados do médico');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Médicos</h1>
          <p className="mt-1 text-sm text-gray-500">
            {clinic && (
              <>
                Clínica: <span className="font-semibold">{clinic.name}</span> • {' '}
              </>
            )}
            {total} {total === 1 ? 'médico cadastrado' : 'médicos cadastrados'}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Novo Médico
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Médico
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Especialidade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {doctors.map((doctor) => (
              <tr key={doctor.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserCircle className="text-gray-400 mr-3" size={20} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{doctor.name}</div>
                      {doctor.crm && (
                        <div className="text-sm text-gray-500">{doctor.crm}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {doctor.specialty ? (
                    <div className="flex items-center text-sm text-gray-900">
                      <Stethoscope className="text-gray-400 mr-2" size={16} />
                      {doctor.specialty}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {doctor.email && (
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="text-gray-400 mr-2 flex-shrink-0" size={14} />
                        {doctor.email}
                      </div>
                    )}
                    {doctor.phone && (
                      <div className="flex items-center text-sm text-gray-900">
                        <Phone className="text-gray-400 mr-2 flex-shrink-0" size={14} />
                        {doctor.phone}
                      </div>
                    )}
                    {!doctor.email && !doctor.phone && (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleStatus(doctor)}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      doctor.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {doctor.isActive ? 'Ativo' : 'Inativo'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleManageClinics(doctor)}
                      className="text-purple-600 hover:text-purple-900"
                      title="Gerenciar Clínicas"
                    >
                      <Building2 size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(doctor)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(doctor)}
                      className="text-red-600 hover:text-red-900"
                      title="Excluir"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {doctors.length === 0 && (
          <div className="text-center py-12">
            <UserCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum médico cadastrado</h3>
            <p className="mt-1 text-sm text-gray-500">Comece criando um novo médico.</p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} />
                Novo Médico
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {isModalOpen && (
        <DoctorFormModal
          doctor={selectedDoctor}
          currentClinic={clinic}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            loadDoctors();
            setIsModalOpen(false);
          }}
        />
      )}

      {isClinicsModalOpen && selectedDoctor && (
        <ManageClinicsModal
          doctor={selectedDoctor}
          onClose={() => {
            setIsClinicsModalOpen(false);
            setSelectedDoctor(null);
          }}
          onSuccess={async () => {
            // Reload the list of doctors
            loadDoctors();
            // Reload the selected doctor to update clinic associations in the modal
            try {
              const updatedDoctor = await doctorsService.getById(selectedDoctor.id);
              setSelectedDoctor(updatedDoctor);
            } catch (error) {
              console.error('Error reloading doctor:', error);
            }
          }}
        />
      )}

      {isDeleteDialogOpen && doctorToDelete && (
        <DeleteConfirmDialog
          doctorName={doctorToDelete.name}
          onConfirm={confirmDelete}
          onCancel={() => {
            setIsDeleteDialogOpen(false);
            setDoctorToDelete(null);
          }}
        />
      )}
    </div>
  );
}

// Doctor Form Modal Component
function DoctorFormModal({
  doctor,
  currentClinic,
  onClose,
  onSuccess,
}: {
  doctor: DoctorWithClinics | null;
  currentClinic: Clinic | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<CreateDoctorData | UpdateDoctorData>(() => {
    if (doctor) {
      // For updating, no organizationId needed
      return {
        name: doctor.name || '',
        email: doctor.email || '',
        crm: doctor.crm || '',
        specialty: doctor.specialty || '',
        phone: doctor.phone || '',
        isActive: doctor.isActive ?? true,
      };
    } else {
      // For creating, organizationId from current clinic
      return {
        name: '',
        organizationId: currentClinic?.organizationId || user?.organizationId || '',
        email: '',
        crm: '',
        specialty: '',
        phone: '',
        isActive: true,
      };
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentClinic && !doctor) {
      toast.error('Nenhuma clínica selecionada');
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (doctor) {
        await doctorsService.update(doctor.id, formData);
        toast.success('Médico atualizado com sucesso!');
      } else {
        // Create doctor
        const newDoctor = await doctorsService.create(formData as CreateDoctorData);
        
        // Associate with current clinic
        if (currentClinic) {
          await doctorsService.addToClinic(newDoctor.id, currentClinic.id);
        }
        
        toast.success('Médico criado e associado à clínica com sucesso!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving doctor:', error);
      toast.error(doctor ? 'Erro ao atualizar médico' : 'Erro ao criar médico');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {doctor ? 'Editar Médico' : 'Novo Médico'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Médico *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Dr. João Silva"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CRM
              </label>
              <input
                type="text"
                value={formData.crm}
                onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="CRM-SP 123456"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Especialidade
              </label>
              <input
                type="text"
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Cardiologia"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mail
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="joao.silva@clinica.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="(11) 98765-4321"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Médico ativo
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando...' : doctor ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Manage Clinics Modal Component
function ManageClinicsModal({
  doctor,
  onClose,
  onSuccess,
}: {
  doctor: DoctorWithClinics;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [allClinics, setAllClinics] = useState<Clinic[]>([]);
  const [doctorClinicIds, setDoctorClinicIds] = useState<string[]>(doctor.clinicIds || []);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadClinics();
  }, []);

  // Update local state when doctor prop changes
  useEffect(() => {
    setDoctorClinicIds(doctor.clinicIds || []);
  }, [doctor.clinicIds]);

  const loadClinics = async () => {
    try {
      const clinics = await clinicsService.getAccessible();
      setAllClinics(clinics);
    } catch (error) {
      console.error('Error loading clinics:', error);
      toast.error('Erro ao carregar clínicas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleClinic = async (clinicId: string) => {
    try {
      if (doctorClinicIds.includes(clinicId)) {
        // Prevent removing last clinic
        if (doctorClinicIds.length === 1) {
          toast.error('Médico deve estar associado a pelo menos uma clínica');
          return;
        }
        
        await doctorsService.removeFromClinic(doctor.id, clinicId);
        setDoctorClinicIds(doctorClinicIds.filter(id => id !== clinicId));
        toast.success('Médico removido da clínica');
      } else {
        await doctorsService.addToClinic(doctor.id, clinicId);
        setDoctorClinicIds([...doctorClinicIds, clinicId]);
        toast.success('Médico adicionado à clínica');
      }
      onSuccess();
    } catch (error) {
      console.error('Error toggling clinic:', error);
      toast.error('Erro ao atualizar associação');
    }
  };

  return (
    <div 
      className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Gerenciar Clínicas - {doctor.name}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Selecione as clínicas onde este médico atende
          </p>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : (
            <>
              {doctorClinicIds.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Atenção:</strong> Todo médico deve estar associado a pelo menos uma clínica.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {allClinics.map((clinic) => (
                  <label
                    key={clinic.id}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={doctorClinicIds.includes(clinic.id)}
                      onChange={() => handleToggleClinic(clinic.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
                      {clinic.address && (
                        <div className="text-sm text-gray-500">{clinic.address}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Dialog Component
function DeleteConfirmDialog({
  doctorName,
  onConfirm,
  onCancel,
}: {
  doctorName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div 
      className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar Exclusão</h3>
        <p className="text-sm text-gray-600 mb-6">
          Tem certeza que deseja excluir o médico <strong>{doctorName}</strong>? Esta ação não pode ser desfeita.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}